import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Select,
  Skeleton,
  Space,
  Statistic,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EyeOutlined, HistoryOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  COBRO_GENERACION_ESTADO,
  COBRO_GENERACION_ORIGEN,
  formatCurrency,
  type CobroGeneracionDetail,
  type CobroGeneracionEstado,
  type CobroGeneracionOrigen,
  type CobroGeneracionSummary,
} from '@aid/shared';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { StatusTag, type StatusTagTone } from '../../components/StatusTag';
import { EmptyState } from '../../components/EmptyState';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT, i18nInstance } from '../../locales';
import {
  useCobroGeneracion,
  useCobroGeneracionSchedule,
  useCobrosGeneracionesList,
} from './api/useCobros';
import { Permission } from '@aid/shared';
import { formatCompactDate } from '../../lib/date';
import { serverSortProps, useServerPaginacion } from '../../lib/server-sort';

const PAGE_SIZE = 20;

interface GenerationFilters {
  origen: CobroGeneracionOrigen | undefined;
  estado: CobroGeneracionEstado | undefined;
  fechaDesde: string | undefined;
  fechaHasta: string | undefined;
}

const EMPTY_FILTERS: GenerationFilters = {
  origen: undefined,
  estado: undefined,
  fechaDesde: undefined,
  fechaHasta: undefined,
};

function formatInstant(
  value: Date | string | null,
  timeZone: string,
  locale: string,
  unavailable: string,
): string {
  if (!value) return unavailable;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return unavailable;
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-GT', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(date);
}

function formatDuration(milliseconds: number | null, locale: string, unavailable: string): string {
  if (milliseconds === null) return unavailable;
  const totalSeconds = Math.max(0, milliseconds) / 1_000;
  const unit = totalSeconds >= 60 ? 'minute' : 'second';
  const value = unit === 'minute' ? totalSeconds / 60 : totalSeconds;
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'es-GT', {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
}

function statusTone(status: CobroGeneracionEstado): StatusTagTone {
  switch (status) {
    case COBRO_GENERACION_ESTADO.COMPLETADA:
      return 'success';
    case COBRO_GENERACION_ESTADO.COMPLETADA_CON_ERRORES:
      return 'warning';
    case COBRO_GENERACION_ESTADO.FALLIDA:
      return 'error';
    case COBRO_GENERACION_ESTADO.EN_PROGRESO:
      return 'info';
  }
}

function statusLabel(status: CobroGeneracionEstado, t: ReturnType<typeof useT>): string {
  switch (status) {
    case COBRO_GENERACION_ESTADO.COMPLETADA:
      return t('pages.cobros.historial.statusCompleted');
    case COBRO_GENERACION_ESTADO.COMPLETADA_CON_ERRORES:
      return t('pages.cobros.historial.statusCompletedWithErrors');
    case COBRO_GENERACION_ESTADO.FALLIDA:
      return t('pages.cobros.historial.statusFailed');
    case COBRO_GENERACION_ESTADO.EN_PROGRESO:
      return t('pages.cobros.historial.statusInProgress');
  }
}

function originLabel(origin: CobroGeneracionOrigen, t: ReturnType<typeof useT>): string {
  return origin === COBRO_GENERACION_ORIGEN.CRON
    ? t('pages.cobros.historial.originAutomatic')
    : t('pages.cobros.historial.originManual');
}

function dateLabel(value: Date | string | null, unavailable: string): string {
  return formatCompactDate(value, i18nInstance.language) ?? unavailable;
}

function counterLabel(row: CobroGeneracionSummary, t: ReturnType<typeof useT>): string {
  return (
    `${t('pages.cobros.historial.generatedShort')}: ${row.counters.generated} · ` +
    `${t('pages.cobros.historial.failedShort')}: ${row.counters.failed}`
  );
}

function DetailContent({
  detail,
  timeZone,
  t,
}: {
  detail: CobroGeneracionDetail;
  timeZone: string;
  t: ReturnType<typeof useT>;
}) {
  const unavailable = t('pages.cobros.historial.unavailable');
  const metadataMode = detail.metadata.mode;
  const modeLabel =
    metadataMode === 'CATCH_UP'
      ? t('pages.cobros.historial.modeCatchUp')
      : metadataMode === 'EXACT_DATE'
        ? t('pages.cobros.historial.modeExactDate')
        : unavailable;

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
        }}
      >
        <Statistic
          title={t('pages.cobros.historial.generated')}
          value={detail.counters.generated}
        />
        <Statistic title={t('pages.cobros.historial.skipped')} value={detail.counters.skipped} />
        <Statistic title={t('pages.cobros.historial.failed')} value={detail.counters.failed} />
        <Statistic
          title={t('pages.cobros.historial.credit')}
          value={formatCurrency(detail.counters.creditoAplicado)}
        />
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(130px, 0.8fr) minmax(0, 1.2fr)',
          gap: '10px 16px',
          margin: 0,
        }}
      >
        <Typography.Text type="secondary">{t('pages.cobros.historial.origin')}</Typography.Text>
        <Typography.Text>{originLabel(detail.origen, t)}</Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.status')}</Typography.Text>
        <Typography.Text>
          <StatusTag tone={statusTone(detail.estado)}>{statusLabel(detail.estado, t)}</StatusTag>
        </Typography.Text>
        <Typography.Text type="secondary">
          {t('pages.cobros.historial.businessDate')}
        </Typography.Text>
        <Typography.Text>{dateLabel(detail.fechaNegocio, unavailable)}</Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.mode')}</Typography.Text>
        <Typography.Text>{modeLabel}</Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.actor')}</Typography.Text>
        <Typography.Text>{detail.actor || unavailable}</Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.startedAt')}</Typography.Text>
        <Typography.Text>
          {formatInstant(detail.startedAt, timeZone, i18nInstance.language, unavailable)}
        </Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.finishedAt')}</Typography.Text>
        <Typography.Text>
          {formatInstant(detail.finishedAt, timeZone, i18nInstance.language, unavailable)}
        </Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.duration')}</Typography.Text>
        <Typography.Text>
          {formatDuration(detail.durationMs, i18nInstance.language, unavailable)}
        </Typography.Text>
        <Typography.Text type="secondary">{t('pages.cobros.historial.errorCode')}</Typography.Text>
        <Typography.Text>{detail.errorCode ?? unavailable}</Typography.Text>
      </dl>
      <Alert type="info" showIcon title={t('pages.cobros.historial.detailAvailability')} />
    </Space>
  );
}

export function CobrosGeneracionHistorialPage() {
  const t = useT();
  const navigate = useNavigate();
  const canGenerate = useHasPermission(Permission.COBROS_GENERAR);
  const [filters, setFilters] = useState<GenerationFilters>(EMPTY_FILTERS);
  const { page, sort, handleChange, setPage } =
    useServerPaginacion<CobroGeneracionSummary>(PAGE_SIZE);
  const [detailId, setDetailId] = useState<number | null>(null);

  const scheduleQuery = useCobroGeneracionSchedule();
  const listQuery = useCobrosGeneracionesList({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    ...filters,
    ...sort,
  });

  const detailQuery = useCobroGeneracion(detailId ?? 0);
  const schedule = scheduleQuery.data;
  const lastExecution = schedule?.lastExecution ?? null;
  const unavailable = t('pages.cobros.historial.unavailable');
  const locale = i18nInstance.language;
  const timeZone = schedule?.timeZone ?? 'America/Guatemala';

  const updateFilters = (next: Partial<GenerationFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setPage(1);
  };

  const hasFilters =
    filters.origen !== undefined ||
    filters.estado !== undefined ||
    filters.fechaDesde !== undefined ||
    filters.fechaHasta !== undefined;

  const columns: ColumnsType<CobroGeneracionSummary> = [
    {
      title: t('pages.cobros.historial.execution'),
      key: 'id',
      ...serverSortProps('id', sort),
      width: 90,

      render: (_value: unknown, row: CobroGeneracionSummary) => `#${row.id}`,
    },
    {
      title: t('pages.cobros.historial.origin'),
      key: 'origen',
      ...serverSortProps('origen', sort),
      width: 130,

      render: (_value: unknown, row: CobroGeneracionSummary) => originLabel(row.origen, t),
    },
    {
      title: t('pages.cobros.historial.status'),
      key: 'estado',
      ...serverSortProps('estado', sort),
      width: 190,

      render: (_value: unknown, row: CobroGeneracionSummary) => (
        <StatusTag tone={statusTone(row.estado)}>{statusLabel(row.estado, t)}</StatusTag>
      ),
    },
    {
      title: t('pages.cobros.historial.businessDate'),
      key: 'fechaNegocio',
      ...serverSortProps('fechaNegocio', sort),
      width: 140,

      render: (_value: unknown, row: CobroGeneracionSummary) =>
        dateLabel(row.fechaNegocio, unavailable),
    },
    {
      title: t('pages.cobros.historial.startedAt'),
      key: 'startedAt',
      ...serverSortProps('startedAt', sort),
      width: 190,

      render: (_value: unknown, row: CobroGeneracionSummary) =>
        formatInstant(row.startedAt, timeZone, locale, unavailable),
    },
    {
      title: t('pages.cobros.historial.finishedAt'),
      key: 'finishedAt',
      ...serverSortProps('finishedAt', sort),
      width: 190,

      render: (_value: unknown, row: CobroGeneracionSummary) =>
        formatInstant(row.finishedAt, timeZone, locale, unavailable),
    },
    {
      title: t('pages.cobros.historial.duration'),
      key: 'durationMs',
      width: 110,
      render: (_value: unknown, row: CobroGeneracionSummary) =>
        formatDuration(row.durationMs, locale, unavailable),
    },
    {
      title: t('pages.cobros.historial.counters'),
      key: 'counters',
      width: 190,
      render: (_value: unknown, row: CobroGeneracionSummary) => (
        <Typography.Text style={{ fontVariantNumeric: 'tabular-nums' }}>
          {counterLabel(row, t)}
        </Typography.Text>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      render: (_value: unknown, row: CobroGeneracionSummary) => (
        <Button icon={<EyeOutlined />} onClick={() => setDetailId(row.id)}>
          {t('common.ver')}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('pages.cobros.historial.title')}
        subtitle={t('pages.cobros.historial.subtitle')}
        actions={
          <Space wrap>
            {canGenerate && (
              <Button icon={<HistoryOutlined />} onClick={() => navigate('/facturas/generar')}>
                {t('pages.cobros.generar')}
              </Button>
            )}
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/facturas')}>
              {t('pages.cobros.backToList')}
            </Button>
          </Space>
        }
      />

      <Card title={t('pages.cobros.historial.scheduleTitle')} style={{ marginBottom: 16 }}>
        {scheduleQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title={t('pages.cobros.historial.scheduleError')}
            action={
              <Button icon={<ReloadOutlined />} onClick={() => scheduleQuery.refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : scheduleQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
              }}
            >
              <div>
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  {t('pages.cobros.historial.effectiveExpression')}
                </Typography.Text>
                <Typography.Text code>{schedule?.expression ?? unavailable}</Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  {t('pages.cobros.historial.timeZone')}
                </Typography.Text>
                <Typography.Text>{schedule?.timeZone ?? unavailable}</Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  {t('pages.cobros.historial.businessDate')}
                </Typography.Text>
                <Typography.Text>
                  {dateLabel(schedule?.businessDate ?? null, unavailable)}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  {t('pages.cobros.historial.nextRun')}
                </Typography.Text>
                <Typography.Text>
                  {schedule?.nextScheduleAvailable && schedule.nextScheduledAt
                    ? formatInstant(schedule.nextScheduledAt, timeZone, locale, unavailable)
                    : t('pages.cobros.historial.nextRunUnavailable')}
                </Typography.Text>
              </div>
            </div>
            {schedule?.activeExecution && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
                title={t('pages.cobros.historial.activeRun')}
                description={t('pages.cobros.historial.activeRunDescription', {
                  id: schedule.activeExecution.id,
                })}
                action={
                  <Button onClick={() => setDetailId(schedule.activeExecution?.id ?? null)}>
                    {t('common.ver')}
                  </Button>
                }
              />
            )}
          </>
        )}
      </Card>

      <Card title={t('pages.cobros.historial.lastRunTitle')} style={{ marginBottom: 16 }}>
        {lastExecution ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16,
            }}
          >
            <Statistic
              title={t('pages.cobros.historial.generated')}
              value={lastExecution.counters.generated}
            />
            <Statistic
              title={t('pages.cobros.historial.skipped')}
              value={lastExecution.counters.skipped}
            />
            <Statistic
              title={t('pages.cobros.historial.failed')}
              value={lastExecution.counters.failed}
            />
            <Statistic
              title={t('pages.cobros.historial.duration')}
              value={formatDuration(lastExecution.durationMs, locale, unavailable)}
            />
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.cobros.historial.noLastRun')}
          />
        )}
      </Card>

      <Card title={t('pages.cobros.historial.tableTitle')}>
        <Space wrap size={[8, 8]} style={{ marginBottom: 16 }}>
          <Select<CobroGeneracionOrigen>
            allowClear
            value={filters.origen}
            placeholder={t('pages.cobros.historial.originFilter')}
            style={{ width: 170 }}
            options={[
              {
                value: COBRO_GENERACION_ORIGEN.CRON,
                label: t('pages.cobros.historial.originAutomatic'),
              },
              {
                value: COBRO_GENERACION_ORIGEN.MANUAL,
                label: t('pages.cobros.historial.originManual'),
              },
            ]}
            onChange={(value) => updateFilters({ origen: value })}
          />
          <Select<CobroGeneracionEstado>
            allowClear
            value={filters.estado}
            placeholder={t('pages.cobros.historial.statusFilter')}
            style={{ width: 210 }}
            options={Object.values(COBRO_GENERACION_ESTADO).map((value) => ({
              value,
              label: statusLabel(value, t),
            }))}
            onChange={(value) => updateFilters({ estado: value })}
          />
          <DatePicker.RangePicker
            value={
              filters.fechaDesde || filters.fechaHasta
                ? [
                    filters.fechaDesde ? dayjs(filters.fechaDesde) : null,
                    filters.fechaHasta ? dayjs(filters.fechaHasta) : null,
                  ]
                : null
            }
            placeholder={[t('pages.cobros.historial.dateFrom'), t('pages.cobros.historial.dateTo')]}
            onChange={(dates) =>
              updateFilters({
                fechaDesde: dates?.[0]?.format('YYYY-MM-DD'),
                fechaHasta: dates?.[1]?.format('YYYY-MM-DD'),
              })
            }
          />
          <Button disabled={!hasFilters} onClick={clearFilters}>
            {t('common.clear')}
          </Button>
        </Space>
        {listQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title={t('pages.cobros.historial.listError')}
            action={<Button onClick={() => listQuery.refetch()}>{t('common.retry')}</Button>}
          />
        ) : (
          <DataTable<CobroGeneracionSummary>
            columns={columns}
            dataSource={listQuery.data?.data ?? []}
            rowKey="id"
            loading={listQuery.isLoading}
            onChange={handleChange}
            locale={{
              emptyText: (
                <EmptyState
                  compact
                  title={t('pages.cobros.historial.emptyTitle')}
                  description={t('pages.cobros.historial.emptyDescription')}
                />
              ),
            }}
            pagination={
              listQuery.data && listQuery.data.total > PAGE_SIZE
                ? {
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: listQuery.data.total,
                    showSizeChanger: false,
                    showTotal: (total, range) =>
                      t('pagination.showTotal', { from: range[0], to: range[1], total }),
                    onChange: setPage,
                  }
                : false
            }
          />
        )}
      </Card>

      <Drawer
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        title={
          detailId !== null ? t('pages.cobros.historial.detailTitle', { id: detailId }) : undefined
        }
        placement="right"
        size="min(100vw, 736px)"
        destroyOnHidden
      >
        {detailQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title={t('pages.cobros.historial.detailError')}
            action={<Button onClick={() => detailQuery.refetch()}>{t('common.retry')}</Button>}
          />
        ) : detailQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : detailQuery.data ? (
          <DetailContent detail={detailQuery.data} timeZone={timeZone} t={t} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.cobros.historial.detailUnavailable')}
          />
        )}
      </Drawer>
    </div>
  );
}
